import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, Plus, Users, ArrowLeft, X, Check } from "lucide-react";
import { portalApi, apiError } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuth";
import MediaAttach from "./MediaAttach";
import MediaGrid from "./MediaGrid";
import VoiceRecorder from "./VoiceRecorder";
import VoicePlayer from "./VoicePlayer";
import { toast } from "sonner";

const initials = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const timeOf = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

function NewConversation({ onClose, onCreated }) {
  const [dir, setDir] = useState([]);
  const [picked, setPicked] = useState([]);
  const [name, setName] = useState("");
  useEffect(() => { portalApi.get("/portal/directory").then((r) => setDir(r.data)).catch(() => {}); }, []);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const create = async () => {
    if (picked.length === 0) return;
    const type = picked.length > 1 ? "group" : "dm";
    try {
      const { data } = await portalApi.post("/portal/conversations", { type, member_ids: picked, name: name || null });
      onCreated(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="new-conversation">
      <div className="bg-[#F4EFE6] rounded-2xl w-full max-w-md p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4"><h3 className="font-serif-display text-2xl">New conversation</h3><button onClick={onClose}><X className="w-5 h-5 text-[#8a7a6a]" strokeWidth={1.5} /></button></div>
        {picked.length > 1 && <input value={name} onChange={(e) => setName(e.target.value)} data-testid="group-name" placeholder="Group name (optional)" className="w-full mb-4 bg-white border border-[#D8CEBE] rounded-md px-3 py-2 font-body outline-none focus:border-[#A9724E]" />}
        <div className="space-y-2 mb-5">
          {dir.map((m) => (
            <button key={m.id} onClick={() => toggle(m.id)} data-testid={`pick-member-${m.id}`}
              className={`w-full flex items-center gap-3 p-2.5 rounded-xl border transition-colors ${picked.includes(m.id) ? "border-[#A9724E] bg-[#EAE3D9]" : "border-[#E0D8C9] hover:bg-white/60"}`}>
              <span className="w-8 h-8 rounded-full bg-[#EAE3D9] text-[#A9724E] flex items-center justify-center text-xs font-serif-display">{initials(m.name)}</span>
              <span className="flex-1 text-left font-serif-display text-[#2A2825]">{m.name}</span>
              {picked.includes(m.id) && <Check className="w-4 h-4 text-[#A9724E]" strokeWidth={2} />}
            </button>
          ))}
        </div>
        <button onClick={create} disabled={picked.length === 0} data-testid="create-conversation"
          className="w-full rounded-full bg-[#A9724E] text-[#F4EFE6] py-2.5 text-sm disabled:opacity-50">Start {picked.length > 1 ? "group" : "conversation"}</button>
      </div>
    </div>
  );
}

export default function Messages() {
  const { user } = usePortalAuth();
  const [params] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [media, setMedia] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const endRef = useRef(null);
  const voice = media.filter((m) => m.type === "audio");

  const loadConvs = () => portalApi.get("/portal/conversations").then((r) => setConvs(r.data)).catch(() => {});
  useEffect(() => { loadConvs(); }, []);
  useEffect(() => { const start = params.get("c"); if (start) setActive(start); }, [params]);

  useEffect(() => {
    if (!active) return;
    const load = () => portalApi.get(`/portal/conversations/${active}/messages`).then((r) => setThread(r.data)).catch(() => {});
    load().then(() => loadConvs());
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [active]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread?.messages?.length]);

  const send = async () => {
    if (!text.trim() && media.length === 0) return;
    const payload = { text, media: media.filter((m) => m.type !== "audio"), voice_url: voice[0]?.path || null };
    setText(""); setMedia([]);
    try {
      const { data } = await portalApi.post(`/portal/conversations/${active}/messages`, payload);
      setThread((t) => ({ ...t, messages: [...t.messages, data] }));
      loadConvs();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="portal-messages" className="max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-2">Stay close</p><h1 className="font-serif-display font-light text-4xl md:text-5xl">Messages</h1></div>
        <button onClick={() => setShowNew(true)} data-testid="new-conversation-btn" className="inline-flex items-center gap-2 rounded-full bg-[#A9724E] text-[#F4EFE6] px-4 py-2 text-sm hover:bg-[#925C3B] transition-colors"><Plus className="w-4 h-4" strokeWidth={1.5} />New</button>
      </div>

      <div className="grid md:grid-cols-[300px_1fr] gap-5 bg-white/40 border border-[#E0D8C9] rounded-2xl overflow-hidden" style={{ minHeight: "60vh" }}>
        {/* inbox */}
        <div className={`border-r border-[#E0D8C9] ${active ? "hidden md:block" : ""}`}>
          {convs.length === 0 ? <p className="p-6 text-sm text-[#8a7a6a] font-body">No conversations yet. Tap “New”.</p> : convs.map((c) => (
            <button key={c.id} onClick={() => setActive(c.id)} data-testid={`conv-${c.id}`}
              className={`w-full flex items-center gap-3 p-4 border-b border-[#E0D8C9]/60 text-left transition-colors ${active === c.id ? "bg-[#EAE3D9]" : "hover:bg-white/50"}`}>
              <span className="w-10 h-10 rounded-full bg-[#EAE3D9] text-[#A9724E] flex items-center justify-center text-sm font-serif-display shrink-0">{c.type === "group" ? <Users className="w-4 h-4" strokeWidth={1.5} /> : initials(c.title)}</span>
              <span className="flex-1 min-w-0"><span className="flex items-center justify-between gap-2"><span className="font-serif-display text-[#2A2825] truncate">{c.title}</span>{c.unread > 0 && <span data-testid={`conv-unread-${c.id}`} className="bg-[#A9724E] text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{c.unread}</span>}</span><span className="block text-xs text-[#8a7a6a] font-body truncate">{c.last_message || "No messages yet"}</span></span>
            </button>
          ))}
        </div>

        {/* thread */}
        <div className={`flex flex-col ${!active ? "hidden md:flex" : ""}`}>
          {!active ? <div className="flex-1 flex items-center justify-center text-[#8a7a6a] font-body">Select a conversation</div> : (
            <>
              <div className="flex items-center gap-3 p-4 border-b border-[#E0D8C9]">
                <button onClick={() => { setActive(null); setThread(null); }} className="md:hidden text-[#8a7a6a]"><ArrowLeft className="w-5 h-5" strokeWidth={1.5} /></button>
                <span className="font-serif-display text-lg text-[#2A2825]">{thread?.conversation?.title || "…"}</span>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: "50vh" }}>
                {(thread?.messages || []).map((m) => (
                  <div key={m.id} className={`flex ${m.is_me ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${m.is_me ? "bg-[#A9724E] text-[#F4EFE6]" : "bg-white border border-[#E0D8C9] text-[#2A2825]"}`}>
                      {!m.is_me && thread.conversation.type === "group" && <p className="text-[10px] tracking-overline uppercase text-[#A9724E] mb-0.5">{m.sender_name}</p>}
                      {m.text && <p className="font-body whitespace-pre-wrap leading-relaxed">{m.text}</p>}
                      {m.voice_url && <VoicePlayer path={m.voice_url} />}
                      <MediaGrid media={m.media} />
                      <p className={`text-[10px] mt-1 ${m.is_me ? "text-[#F4EFE6]/70" : "text-[#8a7a6a]"}`}>{timeOf(m.created_at)}</p>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-[#E0D8C9]">
                {voice.length > 0 && <div className="mb-2"><VoicePlayer path={voice[0].path} /></div>}
                <div className="flex items-center gap-2">
                  <MediaAttach testid="msg-media" value={media.filter((m) => m.type !== "audio")} onChange={(vis) => setMedia([...voice, ...vis])} scope="private" />
                  <VoiceRecorder testid="msg-voice" onRecorded={(path) => setMedia([{ type: "audio", path }])} />
                  <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="msg-input"
                    placeholder="Write a message…" className="flex-1 bg-white border border-[#D8CEBE] rounded-full px-4 py-2.5 font-body outline-none focus:border-[#A9724E]" />
                  <button onClick={send} data-testid="msg-send" className="rounded-full bg-[#A9724E] text-[#F4EFE6] p-2.5 hover:bg-[#925C3B] transition-colors"><Send className="w-4 h-4" strokeWidth={1.5} /></button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showNew && <NewConversation onClose={() => setShowNew(false)} onCreated={(c) => { setShowNew(false); loadConvs(); setActive(c.id); }} />}
    </div>
  );
}
